import React from 'react';
import { Link } from 'react-router-dom';
import { FooterSection } from '../components/landing';
import { usePageTitle } from '../hooks/usePageTitle';

const TermsOfServicePage: React.FC = () => {
  usePageTitle('Terms of Service');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-semibold mb-6 backdrop-blur-sm border border-white/20">
              Legal
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-300">
              Please read these terms carefully before using Retrosive.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 11, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-gray-600">

            {/* 1. Acceptance */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p>
                These Terms of Service ("Terms") govern your access to and use of the Retrosive
                platform, including the website at <strong>retrosive.com</strong> and all related
                services (collectively, the "Service"), operated by Retrosive ("we", "our", or "us").
              </p>
              <p className="mt-4">
                By creating an account or using the Service, you agree to be bound by these Terms.
                If you do not agree to these Terms, you may not use the Service.
              </p>
            </div>

            {/* 2. Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p>
                Retrosive is a real-time collaborative platform designed for team retrospectives.
                The Service allows users to create boards, add cards, vote, discuss, and generate
                action items as part of agile retrospective workflows. The Service is available
                through various subscription plans, including a free tier.
              </p>
            </div>

            {/* 3. Account Registration */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
              <p>To use certain features of the Service, you must create an account. You agree to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="mt-4">
                You must be at least 16 years of age to create an account and use the Service.
              </p>
            </div>

            {/* 4. Subscription and Payment */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription Plans and Payment</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1 Plans</h3>
              <p>
                The Service is offered through multiple subscription tiers (Free, Starter, Pro,
                and Business). Each plan has different features, usage limits, and pricing as
                described on our{' '}
                <Link to="/pricing" className="text-orange-500 hover:text-orange-600 font-medium">
                  Pricing page
                </Link>.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Billing</h3>
              <p>
                Paid subscriptions are billed on a recurring basis (monthly or annually) depending
                on the billing cycle you select. Payments are processed securely through Stripe.
                By subscribing to a paid plan, you authorize us to charge the applicable fees to
                your designated payment method.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Price Changes</h3>
              <p>
                We reserve the right to modify pricing at any time. Any price changes will be
                communicated to you in advance and will take effect at the beginning of your next
                billing cycle.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.4 Free Trial</h3>
              <p>
                We may offer free trial periods for paid plans. At the end of the trial period,
                your subscription will automatically convert to a paid subscription unless you
                cancel before the trial ends.
              </p>
            </div>

            {/* 5. Acceptable Use */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
              <p>You agree not to use the Service to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Violate any applicable law or regulation</li>
                <li>Infringe on the intellectual property rights of others</li>
                <li>Upload or transmit malicious code, viruses, or harmful content</li>
                <li>Attempt to gain unauthorized access to other accounts, systems, or networks</li>
                <li>Interfere with or disrupt the integrity or performance of the Service</li>
                <li>Use the Service for spam, phishing, or other fraudulent purposes</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Scrape, crawl, or use automated means to access the Service without permission</li>
                <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
                <li>Circumvent rate limits or other usage restrictions</li>
              </ul>
              <p className="mt-4">
                We reserve the right to suspend or terminate accounts that violate these terms.
              </p>
            </div>

            {/* 6. User Content */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Content</h2>
              <p>
                You retain ownership of all content you create through the Service, including
                board content, cards, comments, and action items ("User Content").
              </p>
              <p className="mt-4">
                By using the Service, you grant us a limited, non-exclusive license to host,
                store, and display your User Content solely for the purpose of operating and
                providing the Service to you and your team members.
              </p>
              <p className="mt-4">
                You are responsible for all User Content you submit. You represent and warrant
                that you have the right to submit such content and that it does not violate
                any third-party rights.
              </p>
            </div>

            {/* 7. Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
              <p>
                The Service, including its design, features, code, documentation, logos, and
                trademarks, is owned by Retrosive and protected by intellectual property laws.
                You may not copy, modify, distribute, sell, or lease any part of the Service
                without our prior written consent.
              </p>
            </div>

            {/* 8. Privacy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Privacy</h2>
              <p>
                Your use of the Service is also governed by our{' '}
                <Link to="/privacy" className="text-orange-500 hover:text-orange-600 font-medium">
                  Privacy Policy
                </Link>, which describes how we collect, use, and protect your information.
                By using the Service, you consent to the practices described in the Privacy Policy.
              </p>
            </div>

            {/* 9. Service Availability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Service Availability</h2>
              <p>
                We strive to maintain high availability of the Service, but we do not guarantee
                that the Service will be uninterrupted, error-free, or available at all times.
                We may perform scheduled maintenance, updates, or modifications to the Service
                that may temporarily affect availability.
              </p>
              <p className="mt-4">
                We reserve the right to modify, suspend, or discontinue any part of the Service
                at any time, with reasonable notice where possible.
              </p>
            </div>

            {/* 10. Cancellation and Termination */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Cancellation and Termination</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.1 By You</h3>
              <p>
                You may cancel your subscription at any time through your account settings.
                Upon cancellation, your subscription will remain active until the end of the
                current billing period. After that, your account will be downgraded to the
                Free plan.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.2 By Us</h3>
              <p>
                We may suspend or terminate your account if you violate these Terms or engage
                in activity that harms the Service or other users. We will make reasonable
                efforts to notify you before termination, except in cases of severe violations.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.3 Effect of Termination</h3>
              <p>
                Upon termination, your right to use the Service ceases immediately. We may
                delete your data after a reasonable retention period following account
                termination, in accordance with our Privacy Policy.
              </p>
            </div>

            {/* 11. Disclaimers */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Disclaimers</h2>
              <p className="uppercase font-semibold text-gray-800">
                The Service is provided "as is" and "as available" without warranties of any
                kind, whether express or implied, including but not limited to implied warranties
                of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
              <p className="mt-4">
                We do not warrant that the Service will meet your specific requirements, that
                the Service will be uninterrupted, timely, secure, or error-free, or that the
                results obtained from use of the Service will be accurate or reliable.
              </p>
            </div>

            {/* 12. Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by applicable law, Retrosive and its officers,
                directors, employees, and agents shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages, including but not limited to loss
                of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Your access to or use of (or inability to access or use) the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Any content obtained from the Service</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
              <p className="mt-4">
                Our total liability for any claims arising out of or relating to these Terms
                or the Service shall not exceed the amount you paid to us during the twelve (12)
                months preceding the claim.
              </p>
            </div>

            {/* 13. Indemnification */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Retrosive and its officers,
                directors, employees, and agents from any claims, damages, losses, liabilities,
                and expenses (including reasonable attorney fees) arising from your use of the
                Service, your violation of these Terms, or your violation of any rights of a
                third party.
              </p>
            </div>

            {/* 14. Governing Law */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of
                the jurisdiction in which Retrosive operates, without regard to its conflict
                of law provisions. Any disputes arising under these Terms shall be subject to
                the exclusive jurisdiction of the courts in that jurisdiction.
              </p>
            </div>

            {/* 15. Changes */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Changes to These Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will provide notice
                of significant changes by posting the updated Terms on this page and updating
                the "Last updated" date. Your continued use of the Service after such changes
                constitutes your acceptance of the new Terms.
              </p>
              <p className="mt-4">
                We encourage you to review these Terms periodically for any updates.
              </p>
            </div>

            {/* 16. General */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. General Provisions</h2>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>
                  <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy
                  and Refund Policy, constitute the entire agreement between you and Retrosive
                  regarding the Service.
                </li>
                <li>
                  <strong>Severability:</strong> If any provision of these Terms is found to be
                  unenforceable, the remaining provisions will remain in full force and effect.
                </li>
                <li>
                  <strong>Waiver:</strong> Our failure to enforce any right or provision of these
                  Terms will not be considered a waiver of that right or provision.
                </li>
                <li>
                  <strong>Assignment:</strong> You may not assign or transfer these Terms without
                  our prior written consent. We may assign our rights and obligations without
                  restriction.
                </li>
              </ul>
            </div>

            {/* 17. Contact */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Us</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-xl border border-gray-200">
                <p className="font-semibold text-gray-900">Retrosive</p>
                <p className="mt-2">
                  Email:{' '}
                  <a href="mailto:retrosivecom@gmail.com" className="text-orange-500 hover:text-orange-600 font-medium">
                    retrosivecom@gmail.com
                  </a>
                </p>
                <p className="mt-1">
                  Website:{' '}
                  <a href="https://retrosive.com" className="text-orange-500 hover:text-orange-600 font-medium">
                    retrosive.com
                  </a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default TermsOfServicePage;
